/**
 * useEpgContactFilter Hook
 *
 * Filters EPG contacts based on organization event selections and contact preferences.
 *
 * Logic:
 * 1. Get all 15DP organizations from EPG API
 * 2. Filter orgs where the specified month has the target event type selected
 * 3. Fetch contacts for matching organizations
 * 4. Filter contacts whose event_types preference includes the target type
 * 5. Deduplicate by email address
 *
 * Created: 2026-01-12
 */

import { useState, useCallback } from 'react';
import { epgApi } from '@shared/services/epgApi';
import type { EPGContact } from '@shared/types/epg';
// Note: FIELD_KEY_MAPPINGS not needed - "Sporting"/"Concerts" are contact-level preferences,
// not organization-level EPG fields

/**
 * Extended EPGContact with organization name and event type match flag for display
 */
export interface EPGContactWithOrg extends EPGContact {
  organization_name: string;
  /** Whether this contact matches the selected event types (for checkbox pre-selection) */
  matchesSelectedEventTypes: boolean;
}

/**
 * Maps HMC event_type to EPG event_types string value
 * These match the checkboxes in EPG Contact panel
 */
export const HMC_TO_EPG_TYPE: Record<string, string> = {
  // Sports teams -> "Sporting"
  'dynamo': 'Sporting',
  'dash': 'Sporting',
  'rockets': 'Sporting',
  'astros': 'Sporting',
  'texans': 'Sporting',
  // Concert/music events -> "Concerts"
  'concert': 'Concerts',
  'comedy': 'Concerts',
  'performance': 'Concerts',
  'show': 'Concerts',
};

/**
 * Month code constants matching EPG field mappings
 */
export const MONTH_CODES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;
export type MonthCode = typeof MONTH_CODES[number];

/**
 * Extract month code from date string
 */
export function getMonthCodeFromDate(dateStr: string): MonthCode {
  const date = new Date(dateStr);
  return MONTH_CODES[date.getMonth()];
}

/**
 * Map HMC event type to EPG event type label
 */
export function mapHMCEventTypeToEPG(hmcEventType: string): string | null {
  const normalized = hmcEventType.toLowerCase().trim();

  // Direct mapping
  if (HMC_TO_EPG_TYPE[normalized]) {
    return HMC_TO_EPG_TYPE[normalized];
  }

  // Check if the event type contains any known keywords
  for (const [keyword, epgType] of Object.entries(HMC_TO_EPG_TYPE)) {
    if (normalized.includes(keyword)) {
      return epgType;
    }
  }

  return null;
}

/**
 * Check if a contact's event_types includes the target type
 */
export function contactMatchesEventType(contact: EPGContact, targetType: string): boolean {
  if (!contact.event_types) return false;

  const contactTypes = contact.event_types
    .split(',')
    .map(t => t.trim().toLowerCase());

  return contactTypes.includes(targetType.toLowerCase());
}

/**
 * Get primary email from contact (prefer work, then home, then other)
 */
export function getContactPrimaryEmail(contact: EPGContact): string | null {
  return contact.email_work || contact.email_home || contact.email_other || null;
}

export interface UseEpgContactFilterResult {
  contacts: EPGContactWithOrg[];
  isLoading: boolean;
  error: string | null;
  /**
   * Load contacts filtered by event types and months
   * @param eventTypes - Contact preference categories ("Sporting", "Concerts")
   * @param months - Month codes to filter by
   * @param specificEventIds - Optional specific event option IDs for organization-level filtering
   */
  loadContacts: (eventTypes: string[], months: MonthCode[], specificEventIds?: number[]) => Promise<void>;
  clearContacts: () => void;
}

/**
 * Hook for filtering EPG contacts by event type and month
 *
 * Uses the backend filtered endpoint to return contacts ONLY from organizations
 * that have the specified event types selected in the given months.
 */
export function useEpgContactFilter(): UseEpgContactFilterResult {
  const [contacts, setContacts] = useState<EPGContactWithOrg[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadContacts = useCallback(async (eventTypes: string[], months: MonthCode[], specificEventIds?: number[]) => {
    if (eventTypes.length === 0 || months.length === 0) {
      setContacts([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use filtered endpoint - returns contacts ONLY from organizations
      // that have the specified event types selected in the given months
      console.log(`[EPG Filter] Fetching filtered contacts: eventTypes=${eventTypes.join(',')}, months=${months.join(',')}, specificEventIds=${specificEventIds?.join(',') || 'all'}`);
      const startTime = Date.now();

      // Call the filtered endpoint with event categories (Sporting, Concerts)
      // If specificEventIds provided, they override the category-based filtering
      const result = await epgApi.fetchFilteredContacts(months, eventTypes, 'Events', specificEventIds);

      console.log(`[EPG Filter] Filtered fetch completed in ${Date.now() - startTime}ms, got ${result.contacts.length} contacts from ${result.filteredOrgCount} orgs`);

      // Process contacts: dedupe by email and mark event type matches
      const allContacts: EPGContactWithOrg[] = [];
      const seenEmails = new Set<string>();
      let contactsMatchingType = 0;

      for (const contact of result.contacts) {
        // Check if contact has any of the target event types (Sporting/Concerts)
        // This is for checkbox pre-selection in the UI
        const hasMatchingType = eventTypes.some(type =>
          contactMatchesEventType(contact, type)
        );

        if (hasMatchingType) {
          contactsMatchingType++;
        }

        // Get primary email for deduplication
        const email = getContactPrimaryEmail(contact);

        // Skip contacts without email or duplicates
        if (email && !seenEmails.has(email.toLowerCase())) {
          seenEmails.add(email.toLowerCase());
          allContacts.push({
            ...contact,
            organization_name: contact.organization || '',
            matchesSelectedEventTypes: hasMatchingType,
          });
        }
      }

      console.log(`[EPG Filter] Processed ${result.contacts.length} contacts from ${result.filteredOrgCount} filtered orgs, ${contactsMatchingType} match event types, ${allContacts.length} unique with email`);

      setContacts(allContacts);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load contacts';
      setError(message);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearContacts = useCallback(() => {
    setContacts([]);
    setError(null);
  }, []);

  return {
    contacts,
    isLoading,
    error,
    loadContacts,
    clearContacts,
  };
}

export default useEpgContactFilter;
